import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import api from "../../lib/api";
import { LumiMascot } from "../../components/Brand";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";

export default function Assignments() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});

  async function load() {
    const r = await api.get("/student/assignments");
    setList(r.data);
  }
  useEffect(() => { load(); }, []);

  async function submit() {
    try {
      await api.post("/student/submit", {
        assignment_id: active.id,
        answers: Object.entries(answers).map(([i, a]) => ({ index: Number(i), answer: a })),
      });
      toast.success(t("assignments.submitOk"));
      setActive(null); setAnswers({});
      load();
    } catch (e) { toast.error(t("errors.generic")); }
  }

  return (
    <AppShell isStudent>
      <h1 className="font-student font-bold text-3xl text-slate-900">{t("assignments.title")}</h1>
      {list.length === 0 && (
        <div className="hb-card p-8 mt-4 flex items-center gap-4">
          <LumiMascot size={80} />
          <div>
            <div className="font-student font-bold text-xl text-slate-900">{t("assignments.none")}</div>
            <div className="text-sm text-slate-600">{t("assignments.noneBody")}</div>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {list.map((a) => (
          <div key={a.id} className="hb-card p-5" data-testid={`assignment-${a.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-adult font-bold text-slate-900">{a.title}</div>
                <div className="text-xs text-slate-500">{a.subject} · {t("student.grade")} {a.grade_level}</div>
              </div>
              {a.submitted ? (
                <span className="text-green-700 text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5"/> {t("assignments.submitted")}</span>
              ) : a.due_date ? (
                <span className="text-slate-600 text-xs font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> {new Date(a.due_date).toLocaleDateString()}</span>
              ) : null}
            </div>
            {a.description && <p className="text-sm text-slate-700 mt-2">{a.description}</p>}
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => setActive(a)} data-testid={`open-assignment-${a.id}`}>{t("assignments.openAssignment")}</Button>
              <Link to={`/app/student/ask?subject=${a.subject}`}><Button size="sm" variant="outline" className="rounded-full">{t("nav.askLumi")}</Button></Link>
            </div>
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="assignment-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-adult font-bold text-xl text-slate-900">{active.title}</div>
                <div className="text-xs text-slate-500">{active.subject} · {t("student.grade")} {active.grade_level}</div>
              </div>
              <button onClick={() => setActive(null)} aria-label={t("common.close")}>×</button>
            </div>
            <p className="text-slate-700 text-sm mt-2">{active.description}</p>
            <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {(active.questions || []).map((q, i) => (
                <div key={`aq-${active.id}-${i}`} className="border border-slate-200 rounded-xl p-3">
                  <div className="font-semibold text-slate-900 text-sm">{i + 1}. {q.q}</div>
                  <Textarea placeholder={t("assignments.answerPlaceholder")}
                            className="mt-2"
                            data-testid={`assignment-answer-${i}`}
                            value={answers[i] || ""}
                            onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setActive(null)}>{t("common.cancel")}</Button>
              <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={submit} data-testid="assignment-submit">{t("common.submit")}</Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
